import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import {
    addUpdateGeojsonLayers,
    removeSourcesIfExists,
    removeLayersIfExists,
    addUpdateGeojsonSource,
} from "@/lib/utils/map";
import useMap from "@/lib/map/useMap";
import { roundTo } from "@/lib/utils/numbers";
import { calculateYBands } from "@/lib/utils/sensitivity";
import { ImageAnalytics } from "@/types/imagery";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FillLayerSpecification, SymbolLayerSpecification } from "mapbox-gl";
import { LineLayerSpecification } from "mapbox-gl";
import { UseQueryResult } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import useAuth from "@/lib/auth/useAuth";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import TableV2 from "@/components/table/TableV2";

const TABLE_SOURCE = "image-analytics-table-source";
const FILL_LAYER: FillLayerSpecification = {
    source: TABLE_SOURCE,
    id: "image-analytics-table-fill",
    type: "fill",
    filter: ["==", ["geometry-type"], "Polygon"],
    layout: {},
    paint: {
        "fill-color": ["get", "color"],
        "fill-opacity": 0.8,
    },
};
const LINE_LAYER: LineLayerSpecification = {
    source: TABLE_SOURCE,
    id: "image-analytics-table-line",
    type: "line",
    layout: {},
    paint: {
        "line-color": "#FFF",
        "line-width": 3,
    },
    minzoom: 14,
};
const SYMBOL_LAYER: SymbolLayerSpecification = {
    source: TABLE_SOURCE,
    id: "image-analytics-table-symbol",
    type: "symbol",
    layout: {
        "text-field": ["get", "pair_id"],
        "text-size": 12,
    },
};

type PlotRow = {
    index: number;
    id: string | number | undefined;
    pair_id: string | number | null;
    plot_type: string;
    sensitized_si: number | null;
    ndre_mean: number;
    zone_number: string | number;
    area_rep: number;
    percent_area_rep: number;
    [key: string]: unknown;
};

function SIBarTable({
    imageAnalyticsQuery,
    pairIds,
}: {
    imageAnalyticsQuery: UseQueryResult<ImageAnalytics>;
    pairIds: string[] | undefined;
}) {
    const { t } = useTranslation("insights");
    const { fieldQuery } = useFieldDashboard() ?? {};
    const { isGrower } = useAuth();

    const plots = useMemo((): PlotRow[] => {
        return (
            imageAnalyticsQuery?.data?.features
                .map((plot, index) => ({
                    ...plot.properties,
                    index,
                    sensitized_si: plot.properties.sensitized_si
                        ? roundTo(plot.properties.sensitized_si * 100, 1)
                        : null,
                    ndre_mean: roundTo(plot.properties.ndre_mean, 4),
                    area_rep: roundTo(plot.properties.area_rep, 1),
                    percent_area_rep: roundTo(
                        plot.properties.percent_area_rep,
                        1,
                    ),
                    id: plot.id,
                }))
                .filter((plot) => {
                    if (!pairIds?.length) return plot;
                    return pairIds.includes(plot.pair_id?.toString() ?? "asdf");
                })
                .filter((plot) => {
                    return !isGrower || plot.plot_type !== "Bulk";
                }) ?? []
        );
    }, [imageAnalyticsQuery?.data, pairIds, isGrower]);

    const bands = useMemo(
        () =>
            calculateYBands(
                fieldQuery?.data?.insight_sensitivity ?? "moderate",
            ),
        [fieldQuery?.data?.insight_sensitivity],
    );

    const { map, mapLoaded } = useMap();

    useEffect(() => {
        if (!map || !mapLoaded) return;

        addUpdateGeojsonSource(map, TABLE_SOURCE, {
            type: "geojson",
            data: { type: "FeatureCollection", features: [] },
        });

        addUpdateGeojsonLayers(map, [FILL_LAYER, LINE_LAYER, SYMBOL_LAYER]);

        return () => {
            if (map && mapLoaded) {
                removeLayersIfExists(map, [
                    FILL_LAYER.id,
                    LINE_LAYER.id,
                    SYMBOL_LAYER.id,
                ]);
                removeSourcesIfExists(map, [TABLE_SOURCE]);
            }
        };
    }, [map, mapLoaded]);

    const onRowMouseEnter = useCallback(
        (row: PlotRow) => {
            if (!map || !mapLoaded) return;
            const feature = imageAnalyticsQuery.data?.features.find(
                (f) => f.id === row.id,
            );
            if (!feature) return;
            const color =
                bands.find((band) => {
                    return (
                        feature.properties?.sensitized_si != null &&
                        feature.properties.sensitized_si * 100 >= band.y0 &&
                        feature.properties.sensitized_si * 100 < band.y1
                    );
                })?.mapColor || "gray";
            addUpdateGeojsonSource(map, TABLE_SOURCE, {
                type: "geojson",
                data: {
                    ...feature,
                    properties: { ...feature.properties, color },
                },
                tolerance: 0,
            });
        },
        [map, mapLoaded, imageAnalyticsQuery.data?.features, bands],
    );

    const [sorting, setSorting] = useState<SortingState>([
        { id: "pair_id", desc: false },
    ]);

    const columns = useMemo<ColumnDef<PlotRow>[]>(
        () => [
            { accessorKey: "pair_id", header: t("tableColumns.pairId"), size: 60 },
            { accessorKey: "plot_type", header: t("tableColumns.plotType"), size: 80 },
            {
                accessorKey: "sensitized_si",
                header: t("tableColumns.siPercent"),
                size: 80,
                cell: ({ getValue }) => {
                    const si = getValue() as number;
                    const color =
                        bands.find((band) => si > band.y0 && si <= band.y1)
                            ?.color ?? "white";
                    return (
                        <div
                            style={{ backgroundColor: color }}
                            className="rounded-md font-medium p-1 text-center text-xs"
                        >
                            {si}%
                        </div>
                    );
                },
            },
            { accessorKey: "ndre_mean", header: t("tableColumns.ndre"), size: 80 },
            { accessorKey: "zone_number", header: t("tableColumns.zone"), size: 60 },
            { accessorKey: "area_rep", header: t("tableColumns.rangeAc"), size: 80 },
            { accessorKey: "percent_area_rep", header: t("tableColumns.weightPercent"), size: 100 },
        ],
        [t, bands],
    );

    return (
        <div className="h-[500px] text-xs">
            <TableV2
                data={plots}
                columns={columns}
                sorting={sorting}
                setSorting={setSorting}
                isLoading={imageAnalyticsQuery.isFetching}
                estimateSize={40}
                onRowMouseEnter={onRowMouseEnter}
            />
        </div>
    );
}

export default SIBarTable;
